import TaskSeries from "../../entities/taskSeries";

/**
 * TaskSeries actions
 *
 * Call order:
 *
 * TASKSERIES_SAVE_QUEUE:	adds the item to the save queue
 * --> TASKSERIES_SAVE:	modifies the state
 * --> TASKSERIES_SAVE_SUCCESS: if all went well
 * --> TASKSERIES_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail TASKSERIES_SAVE_QUEUE is during the call to TASKSERIES_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at TASKSERIES_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: TASKSERIES_SAVE_SUCCESS, TASKSERIES_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or TASKSERIES_PENDING_CHECK (it left the queue).
 */

export const TASKSERIES_FETCH_ONE_QUEUE: any = 'TASKSERIES_FETCH_ONE_QUEUE';
export const TASKSERIES_FETCH_SUCCESS: any = 'TASKSERIES_FETCH_SUCCESS';
export const TASKSERIES_FETCH_FAILURE: any = 'TASKSERIES_FETCH_FAILURE';
export const TASKSERIES_FETCH_ONE_SUCCESS: any = 'TASKSERIES_FETCH_ONE_SUCCESS';
export const TASKSERIES_FETCH_ONE_FAILURE: any = 'TASKSERIES_FETCH_ONE_FAILURE';

export const TASKSERIES_SAVE: any = 'TASKSERIES_SAVE';
export const TASKSERIES_SAVE_QUEUE: any = 'TASKSERIES_SAVE_QUEUE';
export const TASKSERIES_SAVE_SUCCESS: any = 'TASKSERIES_SAVE_SUCCESS';
export const TASKSERIES_SAVE_FAILURE: any = 'TASKSERIES_SAVE_FAILURE';

export const TASKSERIES_SAVE_MANY: any = 'TASKSERIES_SAVE_MANY';
export const TASKSERIES_SAVE_MANY_QUEUE: any = 'TASKSERIES_SAVE_MANY_QUEUE';
export const TASKSERIES_SAVE_MANY_SUCCESS: any = 'TASKSERIES_SAVE_MANY_SUCCESS';
export const TASKSERIES_SAVE_MANY_FAILURE: any = 'TASKSERIES_SAVE_MANY_FAILURE';

export const TASKSERIES_REMOVE: any = 'TASKSERIES_REMOVE';
export const TASKSERIES_REMOVE_QUEUE: any = 'TASKSERIES_REMOVE_QUEUE';
export const TASKSERIES_REMOVE_SUCCESS: any = 'TASKSERIES_REMOVE_SUCCESS';
export const TASKSERIES_REMOVE_FAILURE: any = 'TASKSERIES_REMOVE_FAILURE';

export const TASKSERIES_RESTORE: any = 'TASKSERIES_RESTORE';
export const TASKSERIES_RESTORE_QUEUE: any = 'TASKSERIES_RESTORE_QUEUE';
export const TASKSERIES_RESTORE_SUCCESS: any = 'TASKSERIES_RESTORE_SUCCESS';
export const TASKSERIES_RESTORE_FAILURE: any = 'TASKSERIES_RESTORE_FAILURE';

export const TASKSERIES_RESTORE_MANY: any = 'TASKSERIES_RESTORE_MANY';
export const TASKSERIES_REMOVE_MANY: any = 'TASKSERIES_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const TASKSERIES_SAVE_REVERT: any = 'TASKSERIES_SAVE_REVERT';
export const TASKSERIES_PENDING_CHECK: any = 'TASKSERIES_PENDING_CHECK';

/* Fetch actions */
export const fetchTaskSeriesSuccess = (taskSerieses: TaskSeries[], partial: boolean, lastFetch: number) => ({type: TASKSERIES_FETCH_SUCCESS, taskSerieses, partial, lastFetch});
export const fetchTaskSeriesFailure = (err: any) => {
		console.error('TaskSeriesAction: fetchTaskSeriesFailure', err);
		return {type: TASKSERIES_FETCH_FAILURE, err};
};
export const fetchOneTaskSeriesQueue = (taskSeriesId: string, sentAddedOn?: number) => ({type: TASKSERIES_FETCH_ONE_QUEUE, taskSeriesId, sentAddedOn});
export const fetchOneTaskSeriesSuccess = (taskSeries: TaskSeries | null, sentAddedOn?: number, taskSeriesId: string = taskSeries?.id) => ({type: TASKSERIES_FETCH_ONE_SUCCESS, taskSeriesId, taskSeries, sentAddedOn});
export const fetchOneTaskSeriesFailure = (id: string, err: any) => {
		console.error('TaskSeriesAction: fetchOneTaskSeriesFailure', err);
		return {type: TASKSERIES_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveTaskSeries = (taskSeries: TaskSeries) => {
    // console.debug('⮂ TASKSERIES_SAVE action', taskSeries?.id);
    return {type: TASKSERIES_SAVE, taskSeries};
}
export const saveTaskSeriesQueue = (taskSeries: TaskSeries, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ TASKSERIES_SAVE_QUEUE action', taskSeries?.id);
    return {type: TASKSERIES_SAVE_QUEUE, taskSeries, onQueued};
}
export const saveTaskSeriesSuccess = (taskSeries: TaskSeries, sentAddedOn?: number) => {
    // console.debug('⮂ TASKSERIES_SAVE_SUCCESS action', taskSeries?.id);
    return {type: TASKSERIES_SAVE_SUCCESS, taskSeries, sentAddedOn};
}
export const saveTaskSeriesFailure = (taskSeries: TaskSeries, status: number, message: string) => {
    console.warn(`⮂ TASKSERIES_SAVE_FAILURE error saving taskSeries: HTTP ${status}: ${message}`, taskSeries?.id);
    return {type: TASKSERIES_SAVE_FAILURE, taskSeries, status, message};
};

/* SaveMany actions */
export const saveManyTaskSerieses = (taskSerieses: TaskSeries[]) => {
    // console.debug('⮂ TASKSERIES_SAVE_MANY action', taskSerieses?.map(taskSeries => taskSeries?.id).join(','));
    return {type: TASKSERIES_SAVE_MANY, taskSerieses};
}
export const saveManyTaskSeriesesQueue = (taskSerieses: TaskSeries[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ TASKSERIES_SAVE_MANY_QUEUE action', taskSerieses?.map(taskSeries => taskSeries.id).join(','));
    return {type: TASKSERIES_SAVE_MANY_QUEUE, taskSerieses, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `taskSerieses` (OWA-1018) */
export const saveManyTaskSeriesesSuccess = (taskSerieses: TaskSeries[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ TASKSERIES_SAVE_MANY_SUCCESS action', taskSerieses?.map(taskSeries => taskSeries.id).join(','));
    return {type: TASKSERIES_SAVE_MANY_SUCCESS, taskSerieses, sentAddedOns};
}
export const saveManyTaskSeriesesFailure = (taskSerieses: TaskSeries[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_TASKSERIESES_FAILURE Error saving taskSerieses: HTTP ${status}: ${message}`, taskSerieses?.map(taskSeries => taskSeries.id).join(','));
		return {type: TASKSERIES_SAVE_MANY_FAILURE, taskSerieses, status, message};
};

/* Remove actions */
export const removeTaskSeries = (taskSeries: TaskSeries) => ({ type: TASKSERIES_REMOVE, taskSeries });
export const removeTaskSeriesQueue = (taskSeries: TaskSeries) => ({ type: TASKSERIES_REMOVE_QUEUE, taskSeries });
export const removeTaskSeriesSuccess = (taskSeries: TaskSeries) => ({ type: TASKSERIES_REMOVE_SUCCESS, taskSeries });
export const removeTaskSeriesFailure = (taskSeries: TaskSeries, status: number, message: string) => {
	console.error(`Redux: removeTaskSeriesFailure Error removing taskSeries: HTTP ${status}: ${message}`);
	return ({ type: TASKSERIES_REMOVE_FAILURE, taskSeries, status, message });
};

/* Restore actions */
export const restoreTaskSeries = (taskSeries: TaskSeries) => ({ type: TASKSERIES_RESTORE, taskSeries });
export const restoreTaskSeriesQueue = (taskSeries: TaskSeries) => ({ type: TASKSERIES_RESTORE_QUEUE, taskSeries });
export const restoreTaskSeriesSuccess = (taskSeries: TaskSeries, sentAddedOn?: number) => ({ type: TASKSERIES_RESTORE_SUCCESS, taskSeries, sentAddedOn });
export const restoreTaskSeriesFailure = (taskSeries: TaskSeries, status: number, message: string) => {
	console.error(`Redux: restoreTaskSeriesFailure Error restoring taskSeries: HTTP ${status}: ${message}`);
	return ({ type: TASKSERIES_RESTORE_FAILURE, taskSeries, status, message });
};

/* RestoreMany actions */
export const restoreManyTaskSerieses = (taskSerieses: TaskSeries[]) => ({ type: TASKSERIES_RESTORE_MANY, taskSerieses });

/* RemoveMany actions */
export const removeManyTaskSerieses = (taskSerieses: TaskSeries[]) => ({ type: TASKSERIES_REMOVE_MANY, taskSerieses });

/* Pending save actions (OWA-1018) */
/**
 * The save `taskSeries` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertTaskSeriesSave = (taskSeries: TaskSeries, options: {sentAddedOn?: number, current?: TaskSeries | null, keep?: boolean} = {}) =>
    ({type: TASKSERIES_SAVE_REVERT, taskSeries, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkTaskSeriesPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: TASKSERIES_PENDING_CHECK, live, refused, readAt});